// Shared shell for public marketing pages (pricing, use-case landing pages):
// the LandingPage's nav + footer + dark atmosphere, extracted so new pages
// don't copy-paste it. LandingPage itself keeps its own copy (it has extra
// hero plumbing) — if a fourth page ever needs a variant, refactor then.
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Zap } from 'lucide-react'

// Subtle dot grid, same as the landing page — atmosphere without noise.
const DOT_GRID_STYLE = {
  backgroundImage: 'radial-gradient(rgba(246,246,249,0.045) 1px, transparent 1px)',
  backgroundSize: '28px 28px',
}

export default function PublicShell({ title, description, children }) {
  // Per-page SEO: title + meta description, restored on unmount.
  useEffect(() => {
    document.title = title
    let meta = document.querySelector('meta[name="description"]')
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'description'
      document.head.appendChild(meta)
    }
    const prev = meta.content
    meta.content = description
    return () => {
      document.title = 'AutoSplit'
      meta.content = prev
    }
  }, [title, description])

  return (
    <div className="min-h-screen bg-ink-950 text-ink-50 overflow-x-hidden" style={DOT_GRID_STYLE}>
      {/* Atmospheric lime glow up top */}
      <div className="pointer-events-none fixed inset-0 z-0" style={{
        background: 'radial-gradient(ellipse 70% 45% at 50% -8%, rgba(200,241,53,0.10) 0%, transparent 60%)',
      }} />

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav className="relative z-10 max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-lime-400 flex items-center justify-center shadow-md shadow-lime-400/25">
            <Zap size={15} className="text-ink-950" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold text-ink-50 tracking-tight">AutoSplit</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="text-sm text-ink-300 hover:text-ink-50 px-3 py-2 rounded-lg hover:bg-ink-800 transition-all duration-150"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-lime-400 text-ink-950 text-sm font-bold hover:bg-lime-500 transition-colors shadow-md shadow-lime-400/20"
          >
            Start free
          </Link>
        </div>
      </nav>

      {children}

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="relative z-10 border-t border-ink-800 mt-10">
        <div className="max-w-5xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-lg bg-lime-400 flex items-center justify-center">
              <Zap size={11} className="text-ink-950" strokeWidth={2.5} />
            </div>
            <span className="font-display text-sm font-semibold text-ink-200">AutoSplit</span>
            <span className="text-xs text-ink-500 ml-2">&middot; Split trips, not friendships</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-ink-400 flex-wrap justify-center">
            <Link to="/pricing" className="hover:text-ink-100 transition-colors">Pricing</Link>
            <Link to="/couples" className="hover:text-ink-100 transition-colors">For couples &amp; roommates</Link>
            <Link to="/split-bank-statement" className="hover:text-ink-100 transition-colors">Split a statement</Link>
            <Link to="/split" className="hover:text-ink-100 transition-colors">Free calculator</Link>
            <Link to="/privacy" className="hover:text-ink-100 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-ink-100 transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
